import { Action } from "redux"

import * as actions from "./actions"
import { IBookmarkButton, IBookmarks } from "./index"
import { Bookmark } from "../../models"

export const initialState: IBookmarks = {
    bookmarks: {},
    bookmarkButtons: {},
    isLoading: false,
    filter: "",
    initialized: false,
    workspaceId: 0,
}

const ACTION_HANDLERS: {
    [action: string]: (state: IBookmarks, props: Action) => IBookmarks
} = {
    [actions.SET_INITIALIZED]: (state): IBookmarks => ({
        ...state,
        initialized: true,
    }),

    [actions.SET_WORKSPACE_ID]: (
        state,
        { workspaceId }: actions.SetWorkspaceId,
    ): IBookmarks => ({ ...state, workspaceId }),

    [actions.REQUEST_BOOKMARKS]: (state): IBookmarks => ({
        ...state,
        isLoading: true,
    }),

    [actions.RECEIVE_BOOKMARKS]: (
        state,
        {
            bookmarks: bookmarksArray,
        }: actions.BookmarkButtonIdWithBookmarkIdActions,
    ): IBookmarks => {
        const bookmarks: Record<string, Bookmark> = {}
        bookmarksArray.forEach((bookmark) => {
            if (!!bookmark.bookmarkId) {
                bookmarks[bookmark.bookmarkId.toString()] = bookmark
            }
        })

        return {
            ...state,
            bookmarks,
            isLoading: false,
        }
    },

    [actions.FILTER_BOOKMARKS]: (
        state,
        { filter }: actions.FilterBookmarks,
    ): IBookmarks => ({ ...state, filter }),

    [actions.INITIALIZE_BOOKMARK_BUTTON]: (
        state,
        {
            bookmarkButtonId,
            bookmarkId,
            parentItemId,
            resourceId,
            resourceType,
            workspaceId,
            url,
        }: actions.InitializeBookmarkButton,
    ): IBookmarks => ({
        ...state,
        bookmarkButtons: {
            ...state.bookmarkButtons,
            [bookmarkButtonId]: {
                bookmarkButtonId,
                bookmarkId,
                isLoading: true,
                parentItemId,
                resourceId,
                resourceType,
                workspaceId,
                url,
            },
        },
    }),

    [actions.REMOVE_BOOKMARK_BUTTON]: (
        state,
        { bookmarkButtonId }: actions.BookmarkButtonIdAction,
    ): IBookmarks => {
        const bookmarkButtons: Record<string, IBookmarkButton> = {
            ...state.bookmarkButtons,
        }
        delete bookmarkButtons[bookmarkButtonId]
        return { ...state, bookmarkButtons }
    },

    [actions.RECEIVE_BOOKMARK_BUTTON_BOOKMARK_ID]: (
        state,
        {
            bookmarkButtonId,
            bookmarkId,
            isLoading,
        }: actions.ReceiveBookmarkButtonBookmarkIdAction,
    ): IBookmarks => {
        const bookmarkButtons = {
            ...state.bookmarkButtons,
            [bookmarkButtonId]: {
                ...state.bookmarkButtons[bookmarkButtonId],
                bookmarkId,
                isLoading,
            },
        }
        return { ...state, bookmarkButtons }
    },

    [actions.ADD_BOOKMARK]: (
        state,
        { bookmarkButton }: actions.BookmarkButtonAction,
    ): IBookmarks => {
        const { bookmarkButtonId } = bookmarkButton

        const bookmarkButtons = {
            ...state.bookmarkButtons,
            [bookmarkButtonId]: {
                ...state.bookmarkButtons[bookmarkButtonId],
                isLoading: true,
            },
        }
        return { ...state, bookmarkButtons }
    },

    [actions.UNBOOKMARK]: (
        state,
        { bookmarkButton }: actions.BookmarkButtonAction,
    ): IBookmarks => {
        const { bookmarkButtonId } = bookmarkButton

        const bookmarkButtons = {
            ...state.bookmarkButtons,
            [bookmarkButtonId]: {
                ...state.bookmarkButtons[bookmarkButtonId],
                isLoading: true,
            },
        }
        return { ...state, bookmarkButtons }
    },

    [actions.RECEIVE_BOOKMARK]: (
        state,
        { bookmark }: actions.BookmarkAction,
    ): IBookmarks => ({
        ...state,
        bookmarks: {
            ...state.bookmarks,
            [bookmark.bookmarkId.toString()]: bookmark,
        },
    }),

    [actions.RECEIVE_REMOVE_BOOKMARK]: (
        state,
        { bookmarkId }: actions.BookmarkIdAction,
    ): IBookmarks => {
        const bookmarks: Record<string, Bookmark> = {
            ...state.bookmarks,
        }
        delete bookmarks[bookmarkId]
        return { ...state, bookmarks }
    },
}

export default function reducer(
    state: IBookmarks = initialState,
    action: Action,
): IBookmarks {
    const handler: (state: IBookmarks, action: Action) => IBookmarks =
        ACTION_HANDLERS[action.type]
    return handler ? handler(state, action) : state
}
